import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Radio,
  Text,
  TextProps,
} from '@everlywell/ui-kit';
import React, { useEffect } from 'react';

export type PurchaseOptionProps = {
  id: string;
  heading: React.ReactNode;
  description: React.ReactNode;
  price: {
    amount: number;
    unitLabel: string; // each, month etc.
  };

  isSelected?: boolean;
  onSelected: (id: string) => void;
  children?: React.ReactNode;
  disableOnSelect?: boolean;
};

/**
 * PurchaseOption component
 */
function PurchaseOption(props: PurchaseOptionProps): JSX.Element {
  const { id, onSelected, isSelected } = props;
  const [dollars, cents] = props.price.amount.toFixed(2).split('.');
  const isExpanded = Boolean(props.isSelected);

  // call onSelect when isSelected changes to true
  useEffect(() => {
    if (isSelected) {
      onSelected(id);
    }
  }, [isSelected, id, onSelected]);

  return (
    <Box
      backgroundColor="tints.lightCream"
      borderColor={isExpanded ? 'viridian.base' : 'transparent'}
      borderRadius="16px"
      borderStyle="solid"
      borderWidth="1px"
      padding={4}
      _focusWithin={{
        borderColor: 'viridian.base',
        outline: 'none',
      }}
    >
      <h3>
        <AccordionButton
          data-testid={`option-${id}`}
          padding={0}
          alignItems="flex-start"
        >
          <Radio
            isChecked={isExpanded}
            // If disableOnSelect is true, remove the circle and make it disabled
            isDisabled={props.disableOnSelect}
            removeCircle={props.disableOnSelect}
          >
            <Box
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
              flex="1"
              gap="1"
            >
              <Box
                alignItems="flex-start"
                display="flex"
                flexDirection="column"
                width="100%"
              >
                <div>{props.heading}</div>
                <div>{props.description}</div>
              </Box>
              <Box
                flexShrink={0}
                display="flex"
                flexDirection="column"
                alignItems="end"
                fontWeight="medium"
              >
                {/* TODO: should use PriceV2, but there is no a way to override styled. Added followup */}
                <PriceComponent
                  dollars={dollars}
                  cents={cents}
                  unitLabel={props.price.unitLabel}
                />
              </Box>
            </Box>
          </Radio>
        </AccordionButton>
      </h3>
      {props.children ? (
        <AccordionPanel paddingBottom={0}>
          <Box as="hr" marginY={4} color="tints.darkCream" />
          {props.children}
        </AccordionPanel>
      ) : null}
    </Box>
  );
}

const withAccordionItem =
  (Component: React.FC<PurchaseOptionProps>) => (props: PurchaseOptionProps) =>
    (
      <AccordionItem border="none">
        {({ isExpanded }) => <Component isSelected={isExpanded} {...props} />}
      </AccordionItem>
    );

export default withAccordionItem(PurchaseOption);

export const PurchaseOptionHeading: React.FC<TextProps> = (props) => (
  <Text fontSize="lg" {...props} />
);

export const PurchaseOptionDescription: React.FC<TextProps> = (props) => (
  <Text fontSize="md" color="tints.darkGrey" {...props} />
);

export type PriceComponentProps = {
  dollars: string;
  cents: string;
  unitLabel: string;
};

export const PriceComponent = ({
  dollars,
  cents,
  unitLabel,
}: PriceComponentProps) => (
  <>
    <Text fontSize="xl" fontWeight="medium" as="h2">
      <Text as="span" fontSize="0.6em" fontWeight="inherit">
        $
      </Text>
      <Text as="span" fontSize="1em" fontWeight="inherit">
        {dollars}
      </Text>
      <Text as="span" fontSize="0.6em" fontWeight="inherit">
        .{cents}
      </Text>
    </Text>
    <Text color="tints.darkGrey" fontSize="sm">
      {' '}
      {unitLabel}
    </Text>
  </>
);
